import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  Formik,
  Field,
  Form,
  ErrorMessage,
} from 'formik';
import NumberFormat from 'react-number-format';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import axios from 'axios';
import { Modal, Spinner } from 'react-bootstrap';
import { isEqual, uniqueId } from 'lodash';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ReactPlayer from 'react-player/lazy';
import parseHtml from 'html-react-parser';
import { bulkListingValidator } from '../admin-dashboard-tables/validators';
import TinyMceField from '../../forms/Partials/TinyMceField';
import { ADS_ADMIN_PREFIX } from '../../../utils/constants';
import { BreedColor, BreedSelect } from '../../../structures/models/breed';
import { UnsavedExternalMedia } from '../../../structures/models/media/external';
import ConfirmationModal from './ConfirmationModal';

interface HistoryState {
  adSearchURL?: string,
}

interface RouteParams {
  ids: string,
  type: string,
}

interface AdTypeConfig {
  default: {
    max_videos: number,
  },
  basic: {
    max_videos: number,
  },
  advanced: {
    max_videos: number,
  },
  premium: {
    max_videos: number,
  }
}

interface NewExternalMedia extends UnsavedExternalMedia {
  temp_id: string,
}

interface ListingValues {
  name?: string,
  price?: number | null,
  sale_price?: number | null,
  deposit_price?: number | null,
  sex?: string,
  colors?: Array<BreedSelect> | null,
  date_available?: string
  expires_at?: string
  remove_can_expire?: false,
  can_expire?: number | boolean,
  registration_type_ids?: number[],
  availability?: string,
  sold_at?: string,
  ad_type?: string,
  label?: string | null,
  description?: string,
  external_video_url?: string,
  external_videos: NewExternalMedia[],
  size?: string,
}

// This type represents the format that the backend accepts
interface ListingValuesRequest extends Omit<ListingValues, 'colors'> {
  colors?: Array<number>,
}

interface ErrorObject {
  key: string,
  value: string[],
}

interface Props extends RouteComponentProps {
  canDelete: boolean,
  canSetExpiration: boolean,
}

const AdsBulkEdit: React.FC<Props> = ({
  history,
  match,
  canDelete,
  canSetExpiration,
}) => {
  const [backURL, setBackURL] = useState(ADS_ADMIN_PREFIX);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalMessage, setModalMessage] = useState<string | ReactElement>('');
  const [fieldsEnabled, setFieldsEnabled] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [errorsArray, setErrorsArray] = useState<ErrorObject[]>([]);
  const [showError, setShowError] = useState(false);
  const [colors, setColors] = useState<BreedColor[] | undefined>();
  const [sizes, setSizes] = useState([]);
  const [adTypeConfig, setAdTypeConfig] = useState<AdTypeConfig | null>(null);
  const [externalMediaLimit, setExternalMediaLimit] = useState(0);
  const [breedsDiff, setBreedsDiff] = useState(false);
  const [listingValues, setListingValues] = useState<ListingValues>({
    name: '',
    price: null,
    sale_price: null,
    deposit_price: null,
    sex: '',
    colors: [],
    date_available: '',
    expires_at: '',
    remove_can_expire: false,
    registration_type_ids: [],
    availability: '',
    sold_at: '',
    ad_type: '',
    label: '',
    description: '',
    size: '',
    external_video_url: '',
    external_videos: [],
  });
  const [adLabels] = useState([
    {
      label: 'Self',
      value: 'none',
    },
    {
      label: 'Email',
      value: 'email',
    },
    {
      label: 'Full Service',
      value: 'full_service',
    },
  ]);

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      border: 'none',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 100,
    }),
  };

  useEffect(() => {
    const registrationsEl = document.getElementById('react-registrations-data');
    if (registrationsEl) {
      const newReg = registrationsEl.dataset.registrations
        ? JSON.parse(registrationsEl.dataset.registrations)
        : [];
      setRegistrations(newReg);
    }
  }, []);

  useEffect(() => {
    if (match.params) {
      const {
        ids,
        type,
      } = match.params as RouteParams;
      if (ids && type) {
        setPageReady(false);
        axios.get(`${ADS_ADMIN_PREFIX}/${type}/${ids}/bulk-edit/data`)
          .then((res) => {
            if (res.status === 200) {
              if (res.data?.breedsDiff) {
                setBreedsDiff(res.data?.breedsDiff);
              }
              if (res.data?.colors) {
                setColors(res.data?.colors);
              }
              if (res.data?.sizes) {
                setSizes(res.data?.sizes);
              }
              if (res.data?.externalMediaLimit) {
                setExternalMediaLimit(res.data?.externalMediaLimit);
              }
              if (res.data?.adTypeConfig) {
                setAdTypeConfig({
                  default: {
                    max_videos: res.data?.externalMediaLimit || 0,
                  },
                  ...res.data?.adTypeConfig,
                });
              }
            }
            setPageReady(true);
          })
          .catch((e) => {
            setPageReady(true);
          });
      }
    }
  }, [match]);

  useEffect(() => {
    if (history?.location) {
      const { state } = history.location;
      if (state) {
        const { adSearchURL } = state as HistoryState;
        if (adSearchURL && adSearchURL !== backURL) {
          setBackURL(adSearchURL);
        }
      }
    }
  }, [history, backURL]);

  const onSortEnd = (result, setFieldValue, videos) => {
    const {
      destination,
      source,
    } = result;
    const { index: newIndex } = destination;
    const { index: oldIndex } = source;

    const newVideos = [...videos];

    const [removed] = newVideos.splice(oldIndex, 1);
    newVideos.splice(newIndex, 0, removed);
    if (!isEqual(newVideos, videos)) {
      setFieldValue('external_videos', newVideos);
    }
  };

  const deleteMedia = (idToDelete, videos, setFieldValue) => {
    const newVideos = [...videos]
      .filter((vid) => vid.temp_id !== idToDelete);
    if (!isEqual(newVideos, videos)) {
      setFieldValue('external_videos', newVideos);
    }
  };

  const onSubmit = useCallback((values) => {
    setListingValues(values);
    setLoading(true);
    setModalMessage((
      <>
        <p>Would you like to update the following fields?</p>
        <ul>
          {
            fieldsEnabled.map((field) => (
              <li
                key={field}
                className="text-capitalize"
              >
                {field.replaceAll('_', ' ')}
              </li>
            ))
          }
        </ul>
      </>
    ));
    setShowConfirm(true);
  }, [fieldsEnabled]);

  const onConfirmSubmit = useCallback(async () => {
    if (match.params) {
      const {
        ids,
        type,
      } = match.params as RouteParams;

      const fieldsToUpdate: ListingValues = { ...listingValues };

      // Double check that user has permission to update expiration
      if (canSetExpiration) {
        if (fieldsToUpdate?.expires_at) {
          fieldsToUpdate.can_expire = 1;
        } else if (fieldsToUpdate?.remove_can_expire) {
          fieldsToUpdate.can_expire = 0;
        }
      } else if (fieldsToUpdate?.expires_at) {
        delete fieldsToUpdate.expires_at;
      }

      delete fieldsToUpdate.remove_can_expire;

      if (fieldsToUpdate?.price) {
        fieldsToUpdate.price *= 100;
      }
      if (fieldsToUpdate?.sale_price) {
        fieldsToUpdate.sale_price *= 100;
      }

      if (fieldsToUpdate?.deposit_price) {
        fieldsToUpdate.deposit_price *= 100;
      }

      if (fieldsToUpdate?.label === 'none') {
        fieldsToUpdate.label = null;
      }

      if (fieldsToUpdate.external_videos.length > 0) {
        fieldsToUpdate.external_videos = [
          ...fieldsToUpdate.external_videos
            .map((video, index) => ({
              ...video,
              sort_id: index + 1,
            })),
        ];
      }

      Object.keys(fieldsToUpdate)
        .forEach((field) => {
          if (!fieldsEnabled.includes(field)) {
            delete fieldsToUpdate[field];
          }
        });

      // Massage data into format accepted by backend
      const requestData: ListingValuesRequest = {
        ...fieldsToUpdate,
        colors: fieldsToUpdate.colors?.map((color) => color.value),
      };

      if (ids && type) {
        await axios.patch(
          `${ADS_ADMIN_PREFIX}/${type}/${ids}`,
          requestData,
        )
          .then(({ data }) => {
            setLoading(false);
            setShowConfirm(false);
            setShowModal(true);
            const {
              message,
              errors = {},
            } = data;
            const tempErrorsArray = Object.keys(errors)
              .map((key) => {
                return {
                  value: Object.values(errors[key])
                    .flat() as string[],
                  key,
                };
              });
            if (!isEqual(errorsArray, tempErrorsArray)) {
              setErrorsArray(tempErrorsArray);
            }
            const parsedMessage = typeof message === 'string'
              ? parseHtml(message)
              : 'Listings updated successfully!';

            setModalMessage(<>{parsedMessage}</>);
            setError('');
          })
          .catch((e) => {
            let message = e?.message;
            if (e?.response?.data) {
              const { response } = e;
              const { data } = response;
              message = data.message;
              const { errors } = data;
              const tempErrorsArray = Object.keys(errors)
                .map((key) => {
                  return {
                    value: Object.values(errors[key])
                      .flat() as string[],
                    key,
                  };
                });
              if (!isEqual(errorsArray, tempErrorsArray)) {
                setErrorsArray(tempErrorsArray);
              }
            }
            setShowConfirm(false);
            setError(message || 'Something went wrong; check your address and connection, then try again.');
            setShowError(true);
            setLoading(false);
          });
      } else {
        setShowConfirm(false);
        setError('Something went wrong; check your address and try again.');
        setShowError(true);
      }
    }
  }, [listingValues, match, canSetExpiration, fieldsEnabled]);

  const updateFieldsEnabled = useCallback((field: string, add: boolean) => {
    const newFields: string[] = [];
    if (add) {
      fieldsEnabled.forEach((currentField) => newFields.push(currentField));
      if (!fieldsEnabled.includes(field)) {
        newFields.push(field);
      }
    } else {
      fieldsEnabled.forEach((currentField) => {
        if (currentField !== field) {
          newFields.push(currentField);
        }
      });
    }
    if (!isEqual(fieldsEnabled, newFields)) {
      setFieldsEnabled(newFields);
    }
  }, [fieldsEnabled, setFieldsEnabled]);

  const ErrorBody = useCallback(() => {
    if (errorsArray.length > 0) {
      return (
        <div className="alert alert-danger" role="alert">
          <ul>
            {
              errorsArray.map((errorObject) => (
                (
                  <li key={errorObject.key}>
                    <p className="text-capitalize mb-0">{errorObject.key}</p>
                    <ul>
                      {errorObject.value.map((errorMessage) => (
                        <li
                          key={errorMessage}
                          className="text-capitalize"
                        >
                          {errorMessage}
                        </li>
                      ))}
                    </ul>
                  </li>
                )
              ))
            }
          </ul>
        </div>
      );
    }
    return <></>;
  }, [errorsArray]);

  const navigateOrCloseModalBasedOnErrorState = useCallback(() => {
    if (!error && errorsArray.length === 0) {
      if (backURL) {
        history.push(backURL);
      } else {
        history.goBack();
      }
    } else {
      setShowModal(false);
    }
  }, [errorsArray, error, backURL, history]);

  const updateMediaLimit = useCallback((adType: string) => {
    if (adTypeConfig) {
      const newLimit = adTypeConfig[adType]?.max_videos;
      if (newLimit !== externalMediaLimit) {
        setExternalMediaLimit(newLimit);
      }
    }
  }, [externalMediaLimit, adTypeConfig]);

  return (
    <div
      style={{ maxWidth: '95%' }}
      className="listings-bulk-edit bulk-edit-outer-wrapper"
    >
      <h1>Bulk Edit Ads</h1>
      {/* Loading Overly */}
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        backdropClassName="bg-blue-200"
        contentClassName="bg-transparent border-0 d-flex align-items-center"
        show={!pageReady}
      >
        <Spinner
          animation="grow"
          variant="blue-600"
        />
      </Modal>
      <Formik
        enableReinitialize
        initialValues={listingValues}
        validationSchema={bulkListingValidator}
        onSubmit={async (values) => onSubmit(values)}
      >
        {
          (formikProps) => {
            const {
              errors,
              touched,
              dirty,
              isValid,
              values,
              setFieldValue,
              setFieldTouched,
              handleChange,
            } = formikProps;
            return (
              <Form>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-ad-type"
                          checked={fieldsEnabled.includes('ad_type')}
                          onChange={() => {
                            updateFieldsEnabled('ad_type', !fieldsEnabled.includes('ad_type'));
                            setFieldValue('ad_type', '');
                            updateMediaLimit('default');
                            setFieldTouched('ad_type', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Ad Type"
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Ad Type
                    </label>
                    <Field name="ad_type">
                      {() => (
                        <Select
                          styles={customSelectStyles}
                          className={`form-control${errors.ad_type && touched.ad_type ? ' is-invalid' : ''}`}
                          placeholder="Choose an ad type..."
                          options={[
                            {
                              label: 'basic',
                              value: 'basic',
                            },
                            {
                              label: 'advanced',
                              value: 'advanced',
                            },
                            {
                              label: 'premium',
                              value: 'premium',
                            },
                          ]}
                          isDisabled={!fieldsEnabled.includes('ad_type')}
                          onChange={(e) => {
                            setFieldValue('ad_type', e?.value);
                            updateMediaLimit(e?.value as string);
                            setFieldTouched('ad_type');
                          }}
                          value={
                            values.ad_type
                              ? {
                                label: values.ad_type,
                                value: values.ad_type,
                              }
                              : null
                          }
                          isClearable={true}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="ad_type" component="div" className="invalid-feedback" />
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-label"
                          checked={fieldsEnabled.includes('label')}
                          onChange={() => {
                            updateFieldsEnabled('label', !fieldsEnabled.includes('label'));
                            setFieldValue('label', '');
                            setFieldTouched('label', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Label."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Ad Label
                    </label>
                    <Field name="label">
                      {() => (
                        <Select
                          styles={customSelectStyles}
                          className={`form-control${errors.label && touched.label ? ' is-invalid' : ''}`}
                          placeholder="Choose an ad label..."
                          options={adLabels}
                          isDisabled={!fieldsEnabled.includes('label')}
                          onChange={(e) => {
                            setFieldValue('label', e?.value);
                            setFieldTouched('label');
                          }}
                          value={
                            values.label
                              ? adLabels.find((adLabel) => adLabel.value === values.label)
                              || null
                              : null
                          }
                          isClearable={true}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="label" component="div" className="invalid-feedback" />
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-name"
                          checked={fieldsEnabled.includes('name')}
                          onChange={() => {
                            updateFieldsEnabled('name', !fieldsEnabled.includes('name'));
                            setFieldValue('name', '');
                            setFieldTouched('name', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Name"
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="name"
                      className="font-weight-bold"
                    >
                      Name
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="name">
                        {({ field }) => (
                          <input
                            className={`form-control${errors.name && touched.name ? ' is-invalid' : ''}`}
                            id="name"
                            type="text"
                            disabled={!fieldsEnabled.includes('name')}
                            {...field}
                            placeholder="Name"
                          />
                        )}
                      </Field>
                      <ErrorMessage name="name" component="div" className="invalid-feedback" />
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-price"
                          checked={fieldsEnabled.includes('price')}
                          onChange={() => {
                            updateFieldsEnabled('price', !fieldsEnabled.includes('price'));
                            setFieldValue('price', '');
                            setFieldTouched('price', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Price"
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="price"
                      className="font-weight-bold"
                    >
                      Price
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="price">
                        {({ field }) => {
                          const {
                            onChange,
                            ...rest
                          } = field;
                          return (
                            <NumberFormat
                              className={`bulk-number form-control${errors.price && touched.price ? ' is-invalid' : ''}`}
                              aria-label="Price."
                              id="price"
                              thousandsGroupStyle="thousand"
                              value={values.price}
                              prefix="$"
                              decimalSeparator="."
                              displayType="input"
                              type="text"
                              thousandSeparator={true}
                              allowNegative={false}
                              decimalScale={2}
                              disabled={!fieldsEnabled.includes('price')}
                              placeholder="$1,000.00"
                              onValueChange={({ floatValue }) => setFieldValue('price', floatValue)}
                              {...rest}
                            />
                          );
                        }}
                      </Field>
                      <ErrorMessage name="price" component="div" className="invalid-feedback" />
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-sex"
                          checked={fieldsEnabled.includes('sex')}
                          onChange={() => {
                            updateFieldsEnabled('sex', !fieldsEnabled.includes('sex'));
                            setFieldValue('sex', '');
                            setFieldTouched('sex', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Sex."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-sm-12 col-xs-12 col-md-6 mb-2">
                    <Field name="sex">
                      {() => (
                        <div className=" radio">
                          <p className="font-weight-bold d-block">
                            Gender
                          </p>
                          <div className="d-flex radio-container">
                            <input
                              type="radio"
                              id="male"
                              name="sex"
                              value="male"
                              checked={values.sex === 'male'}
                              disabled={!fieldsEnabled.includes('sex')}
                              onChange={() => {
                                setFieldValue('sex', 'male');
                                setFieldTouched('sex');
                              }}
                            />
                            {/* eslint-disable-next-line max-len */}
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label
                              className="radio d-flex"
                              htmlFor="male"
                            >
                              <i className="fas fa-mars my-auto mx-1" aria-hidden="true" />
                              Male
                            </label>
                            <input
                              type="radio"
                              id="female"
                              name="sex"
                              value="female"
                              checked={values.sex === 'female'}
                              disabled={!fieldsEnabled.includes('sex')}
                              onChange={() => {
                                setFieldValue('sex', 'female');
                                setFieldTouched('sex');
                              }}
                            />
                            {/* eslint-disable-next-line max-len */}
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label
                              className="radio d-flex"
                              htmlFor="female"
                            >
                              <i className="fas fa-venus my-auto mx-1" aria-hidden="true" />
                              Female
                            </label>
                          </div>
                        </div>
                      )}
                    </Field>
                    <ErrorMessage name="sex" component="div" className="invalid-feedback" />
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-colors"
                          checked={fieldsEnabled.includes('colors')}
                          onChange={() => {
                            updateFieldsEnabled('colors', !fieldsEnabled.includes('colors'));
                            setFieldValue('colors', null);
                            setFieldTouched('colors', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Colors."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Colors
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="colors">
                        {() => (
                          <Select
                            isDisabled={!colors || colors.length === 0 || !fieldsEnabled.includes('colors')}
                            styles={customSelectStyles}
                            className={`form-control${errors.colors && touched.colors ? ' is-invalid' : ''}`}
                            placeholder="Choose a color..."
                            options={colors ? colors.map((color) => {
                              return {
                                label: color.name,
                                value: color.id,
                              };
                            }) : undefined}
                            onChange={(e) => {
                              setFieldValue('colors', e);
                              setFieldTouched('colors');
                            }}
                            value={values.colors}
                            isMulti={true}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="color" component="div" className="invalid-feedback" />
                      {
                        breedsDiff
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-warning">
                              Note: The puppies that you have selected to
                              edit belong to different breeds!
                            </p>
                          )
                          : <></>
                      }
                      {
                        (pageReady && (!colors || colors.length === 0))
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-danger">
                              There are no valid color options for your
                              selection of puppies
                            </p>
                          )
                          : <></>
                      }
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-size"
                          checked={fieldsEnabled.includes('size')}
                          onChange={() => {
                            updateFieldsEnabled('size', !fieldsEnabled.includes('size'));
                            setFieldValue('size', '');
                            setFieldTouched('size', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Size."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Size
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="size">
                        {() => (
                          <Select
                            isClearable={true}
                            isDisabled={sizes.length === 0 || !fieldsEnabled.includes('size')}
                            styles={customSelectStyles}
                            className={`form-control${errors.size && touched.size ? ' is-invalid' : ''}`}
                            placeholder="Choose a size..."
                            options={sizes.map((size: string) => {
                              return {
                                label: size,
                                value: size,
                              };
                            })}
                            onChange={(e) => {
                              setFieldValue('size', e?.value);
                              setFieldTouched('size');
                            }}
                            value={
                              values.size
                                ? {
                                  label: values.size,
                                  value: values.size,
                                }
                                : null
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage name="size" component="div" className="invalid-feedback" />
                      {
                        breedsDiff
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-warning">
                              Note: The puppies that you have selected to
                              edit belong to different breeds!
                            </p>
                          )
                          : <></>
                      }
                      {
                        (pageReady && sizes.length === 0)
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-danger">
                              Could not load sizes options; size setting is
                              disabled.
                            </p>
                          )
                          : <></>
                      }
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-date-available"
                          checked={fieldsEnabled.includes('date_available')}
                          onChange={() => {
                            updateFieldsEnabled('date_available', !fieldsEnabled.includes('date_available'));
                            setFieldValue('date_available', '');
                            setFieldTouched('date_available', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Date Available."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Date Available
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="date_available">
                        {() => (
                          <DatePicker
                            calendarAriaLabel="Toggle calendar for date available."
                            clearAriaLabel="Clear date available."
                            dayAriaLabel="Day available."
                            monthAriaLabel="Month available."
                            yearAriaLabel="Year available."
                            nativeInputAriaLabel="Date available."
                            className={`form-control${errors.date_available && touched.date_available ? ' is-invalid' : ''}`}
                            disabled={!fieldsEnabled.includes('date_available')}
                            onChange={(val) => {
                              setFieldValue('date_available', val);
                              setFieldTouched('date_available');
                            }}
                            value={values.date_available
                              ? new Date(values.date_available)
                              : null}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="date_available"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                </div>
                {canSetExpiration && (
                  <div className="row flex-column flex-md-row m-0">
                    <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                      <div className="toggle-slider-container d-flex align-items-center h-100">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className="toggle-inner-container mb-0"
                        >
                          <input
                            id="toggle-expires-at"
                            checked={fieldsEnabled.includes('expires_at')}
                            onChange={() => {
                              updateFieldsEnabled('expires_at', !fieldsEnabled.includes('expires_at'));
                              setFieldValue('expires_at', '');
                              setFieldTouched('expires_at', false);
                              setFieldValue('remove_can_expire', false);
                              setFieldTouched('remove_can_expire', false);
                            }}
                            type="checkbox"
                            aria-label="Enable Edit Expires At."
                            className="toggle-slider-input"
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>
                    </div>
                    <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <p
                        aria-hidden={true}
                        className="font-weight-bold"
                      >
                        Expires At
                      </p>
                      <div className="d-flex flex-column w-100">
                        <Field name="expires_at">
                          {() => (
                            <DatePicker
                              calendarAriaLabel="Toggle calendar for expiration."
                              clearAriaLabel="Clear date of expiration."
                              dayAriaLabel="Day of expiration."
                              monthAriaLabel="Month of expiration."
                              yearAriaLabel="Year of expiration."
                              nativeInputAriaLabel="Date of expiration."
                              disabled={values.remove_can_expire || !fieldsEnabled.includes('expires_at')}
                              className={`form-control${errors.expires_at && touched.expires_at ? ' is-invalid' : ''}`}
                              onChange={(val) => {
                                setFieldValue('expires_at', val);
                                setFieldTouched('expires_at');
                                if (val && !fieldsEnabled.includes('can_expire')) {
                                  updateFieldsEnabled('can_expire', true);
                                }
                              }}
                              value={values.expires_at
                                ? new Date(values.expires_at)
                                : null}
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="expires_at"
                          component="div"
                          className="invalid-feedback"
                        />
                        {
                          values.remove_can_expire
                            ? (
                              <p
                                id="expires-note"
                                className="text-warning"
                              >
                                Note: You cannot set an Expiration Date if you have
                                elected to remove expiration from listings.
                              </p>
                            )
                            : (
                              <p
                                id="expires-note"
                                className="text-warning"
                              >
                                Note: If expiration date is set,
                                listings will be marked as able to expire.
                              </p>
                            )
                        }
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={values.remove_can_expire}
                            onChange={() => {
                              setFieldValue('remove_can_expire', !values.remove_can_expire);
                              setFieldTouched('remove_can_expire');
                              updateFieldsEnabled('can_expire', !values.remove_can_expire);
                            }}
                            disabled={!!values.expires_at || !fieldsEnabled.includes('expires_at')}
                            id="remove-can-expire"
                            aria-describedby="can-expire-note"
                          />
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label
                            className="form-check-label"
                            htmlFor="remove-can-expire"
                          >
                            Mark ads as "Cannot Expire"
                          </label>
                        </div>
                        <ErrorMessage
                          name="remove_can_expire"
                          component="div"
                          className="invalid-feedback"
                        />
                        {
                          !!values.expires_at && (
                            <p
                              id="can-expire-note"
                              className="text-warning"
                            >
                              Note: If expiration date is set,
                              you cannot remove the ability to expire.
                            </p>
                          )
                        }
                      </div>
                    </div>
                  </div>
                )}
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-sold-at"
                          checked={fieldsEnabled.includes('sold_at')}
                          onChange={() => {
                            if (values.sold_at) {
                              setFieldValue('availability', '');
                              setFieldTouched('availability', false);
                            }
                            updateFieldsEnabled('sold_at', !fieldsEnabled.includes('sold_at'));
                            setFieldValue('sold_at', '');
                            setFieldTouched('sold_at', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Date Sold."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <p
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Date Sold
                    </p>
                    <div className="d-flex flex-column w-100">
                      <Field name="sold_at">
                        {() => (
                          <DatePicker
                            calendarAriaLabel="Toggle calendar for sold."
                            clearAriaLabel="Clear date sold."
                            dayAriaLabel="Day sold."
                            monthAriaLabel="Month sold."
                            yearAriaLabel="Year sold."
                            nativeInputAriaLabel="Date sold."
                            className={`form-control${errors.sold_at && touched.sold_at ? ' is-invalid' : ''}`}
                            disabled={!fieldsEnabled.includes('sold_at')}
                            onChange={(val) => {
                              setFieldValue('sold_at', val);
                              setFieldTouched('sold_at');
                              // If an ad has a sold date, it needs to be marked sold
                              if (val) {
                                setFieldValue('availability', 'sold');
                                setFieldTouched('availability');
                                updateFieldsEnabled('availability', true);
                              } else if (values.availability === 'sold') {
                                setFieldValue('availability', '');
                                setFieldTouched('availability');
                              }
                            }}
                            value={values.sold_at
                              ? new Date(values.sold_at)
                              : null}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="sold_at" component="div" className="invalid-feedback" />
                      {
                        (values.availability === 'sold' && !values.sold_at)
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-warning">
                              Note: Availability is set to 'sold', but there
                              is no sold at date set!
                            </p>
                          )
                          : <></>
                      }
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-availability"
                          checked={fieldsEnabled.includes('availability')}
                          onChange={() => {
                            updateFieldsEnabled('availability', !fieldsEnabled.includes('availability'));
                            setFieldValue('availability', '');
                            setFieldTouched('availability', false);
                          }}
                          type="checkbox"
                          disabled={!!values.sold_at}
                          aria-label="Enable Edit Availability."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Availability
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="availability">
                        {() => (
                          <Select
                            isClearable={true}
                            styles={customSelectStyles}
                            className={`form-control${errors.availability && touched.availability ? ' is-invalid' : ''}`}
                            placeholder="Choose a status..."
                            options={[
                              {
                                label: 'available',
                                value: 'available',
                              },
                              {
                                label: 'sold',
                                value: 'sold',
                              },
                              {
                                label: 'pending',
                                value: 'pending',
                              },
                            ]}
                            onChange={(e) => {
                              setFieldValue('availability', e?.value);
                              setFieldTouched('availability');
                            }}
                            value={
                              values.availability
                                ? {
                                  label: values.availability,
                                  value: values.availability,
                                }
                                : null
                            }
                            isDisabled={!!values.sold_at || !fieldsEnabled.includes('availability')}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="availability"
                        component="div"
                        className="invalid-feedback"
                      />
                      {
                        values.sold_at
                          // eslint-disable-next-line max-len
                          ? (
                            <p className="text-warning">
                              Note: Availability must be set to 'sold' when
                              Date Sold is set!
                            </p>
                          )
                          : <></>
                      }
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-registration"
                          checked={fieldsEnabled.includes('registration_type_ids')}
                          onChange={() => {
                            updateFieldsEnabled('registration_type_ids', !fieldsEnabled.includes('registration_type_ids'));
                            setFieldValue('registration_type_ids', []);
                            setFieldTouched('registration_type_ids', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Registration."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      aria-hidden={true}
                      className="font-weight-bold"
                    >
                      Registrations
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Field name="registration_type_ids">
                        {() => (
                          <Select
                            styles={customSelectStyles}
                            className={`form-control${errors.registration_type_ids && touched.registration_type_ids ? ' is-invalid' : ''}`}
                            placeholder="Select registrations..."
                            options={registrations.map((reg: { name: string, id: number }) => ({
                              label: reg.name,
                              value: reg.id,
                            }))}
                            onChange={(e) => {
                              setFieldValue(
                                'registration_type_ids',
                                e.map((item: { label: string, value: number }) => item.value),
                              );
                              setFieldTouched('registration_type_ids');
                            }}
                            value={
                              (values?.registration_type_ids
                                && Array.isArray(values.registration_type_ids)
                              )
                                ? values.registration_type_ids
                                  .map((value: number) => {
                                    const registrationFound = registrations
                                      .find((reg: { name: string, id: number }) => {
                                        return reg.id === value;
                                      }) || {
                                      name: 'Error',
                                      id: 0,
                                    };
                                    return {
                                      label: registrationFound ? registrationFound?.name : 'Error',
                                      value,
                                    };
                                  })
                                : [{
                                  label: 'Error',
                                  value: 0,
                                }]
                            }
                            isDisabled={!fieldsEnabled.includes('registration_type_ids')}
                            isMulti={true}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="registration_type_ids"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-description"
                          checked={fieldsEnabled.includes('description')}
                          onChange={() => {
                            updateFieldsEnabled('description', !fieldsEnabled.includes('description'));
                            setFieldValue('description', '');
                            setFieldTouched('description', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit Description."
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    <TinyMceField
                      isDisabled={!fieldsEnabled.includes('description')}
                      formikProps={formikProps}
                      fieldName="description"
                      visibleName="Description"
                      onChangeCallback={() => handleChange('description')}
                    />
                  </div>
                </div>
                <div className="row flex-column flex-md-row m-0">
                  <div className="col col-xs-2 col-md-2 col-xl-1 mb-2">
                    <div className="toggle-slider-container d-flex align-items-center h-100">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="toggle-inner-container mb-0"
                      >
                        <input
                          id="toggle-external-video-url"
                          disabled={externalMediaLimit === 0}
                          checked={fieldsEnabled.includes('external_videos')}
                          onChange={() => {
                            updateFieldsEnabled('external_videos', !fieldsEnabled.includes('external_videos'));
                            setFieldValue('external_video_url', '');
                            setFieldValue('external_videos', []);
                            setFieldTouched('external_video_url', false);
                            setFieldTouched('external_videos', false);
                          }}
                          type="checkbox"
                          aria-label="Enable Edit External Video Url"
                          className="toggle-slider-input"
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="external_video_url"
                      className="font-weight-bold"
                    >
                      External Video (YouTube/Vimeo)
                    </label>
                    <div className="d-flex flex-column w-100">
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100">
                          <Field name="external_video_url">
                            {({ field }) => (
                              <input
                                className={`form-control${errors.external_video_url && touched.external_video_url ? ' is-invalid' : ''}`}
                                id="external_video_url"
                                type="text"
                                disabled={!fieldsEnabled.includes('external_videos')}
                                {...field}
                                placeholder="Video URL"
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="external_video_url"
                            component="div"
                            className="invalid-feedback mb-2"
                          />
                        </div>
                        {fieldsEnabled.includes('external_videos') && (
                          <button
                            className="btn btn-primary ml-2"
                            type="button"
                            disabled={
                              !!errors.external_video_url
                              || loading
                              || dragging
                              || values.external_videos.length >= externalMediaLimit
                            }
                            onClick={() => {
                              if (values?.external_video_url) {
                                const youTubeRegex = new RegExp(/^(https:\/\/(www.)?youtube.com\/watch\?v=([^&\n]+)&?)$|^(https:\/\/youtu.be\/([^&\n]+)&?)$/);
                                const vimeoRegex = new RegExp(/(https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/);
                                const isYouTube = youTubeRegex.test(values?.external_video_url);
                                const isVimeo = isYouTube
                                  ? false
                                  : vimeoRegex.test(values.external_video_url);
                                if (!errors.external_video_url && (isYouTube || isVimeo)) {
                                  const newVideos = [
                                    ...values.external_videos,
                                    {
                                      source_type: isYouTube
                                        ? 'youtube'
                                        : 'vimeo',
                                      resource_link: values.external_video_url,
                                      temp_id: uniqueId(isYouTube
                                        ? 'youtube'
                                        : 'vimeo'),
                                    },
                                  ];
                                  setFieldValue('external_videos', newVideos);
                                  setFieldValue('external_video_url', '');
                                  setFieldTouched('external_video_url', false);
                                } else {
                                  setError('Video URL could not be validated; check the URL and try again.');
                                  setShowError(true);
                                }
                              } else {
                                setError('Something went wrong; check your video URL and try again.');
                                setShowError(true);
                              }
                            }}
                          >
                            Add Video
                          </button>
                        )}
                      </div>
                      {
                        (externalMediaLimit === 0)
                          ? (
                            <p className="text-warning mb-2">
                              Note: You have selected one or more ads
                              that do not allow external videos.
                            </p>
                          ) : (
                            <p className="text-warning mb-2">
                              Note: Based on the ads selected, you may upload only
                              {`${externalMediaLimit === 1 ? ' 1 video' : ` ${externalMediaLimit} videos`}.`}
                            </p>
                          )
                      }
                      <div
                        className={`photo-upload-gallery d-flex flex-nowrap col-md-12 bg-white p-3 border rounded ${values.external_videos.length === 0 ? 'hidden' : ''}`}
                      >
                        <DragDropContext
                          onDragStart={() => {
                            setDragging(true);
                          }}
                          onDragEnd={(result) => {
                            setDragging(false);
                            if (!loading) {
                              onSortEnd(result, setFieldValue, values.external_videos);
                            }
                          }}
                        >
                          <Droppable
                            droppableId="droppable"
                            direction="horizontal"
                          >
                            {(provided, snapshot) => (
                              <div
                                className="wrapper d-flex"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {
                                  values.external_videos.map((video, index) => (
                                    <Draggable
                                      key={video.temp_id}
                                      draggableId={video.temp_id}
                                      index={index}
                                    >
                                      {(draggableProvided) => (
                                        <div
                                          ref={draggableProvided.innerRef}
                                          {...draggableProvided.draggableProps}
                                          {...draggableProvided.dragHandleProps}
                                          className="photo-drag"
                                          key={video.temp_id}
                                        >
                                          <div
                                            key={`div-${video.temp_id}`}
                                            className="m-2 photo-uploaded"
                                          >
                                            <div style={{ position: 'relative' }}>
                                              <span
                                                className="position-absolute text-white top-0 end-0 z-10"
                                              >
                                                <button
                                                  type="button"
                                                  style={{ all: 'unset' }}
                                                  onClick={() => {
                                                    if (!loading && !dragging) {
                                                      deleteMedia(
                                                        video.temp_id,
                                                        values.external_videos,
                                                        setFieldValue,
                                                      );
                                                    }
                                                  }}
                                                >
                                                  <i
                                                    className={loading || dragging ? 'hidden' : 'fas fa-times-circle'}
                                                  />
                                                </button>
                                              </span>
                                              <ReactPlayer
                                                url={video.resource_link}
                                                width={160}
                                                height={90}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))
                                }
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col col-sm-12 col-xs-12 col-md-6 mb-2">
                  <div className="row flex-row justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={loading || dragging}
                      onClick={() => {
                        if (backURL) {
                          history.push(backURL);
                        } else {
                          history.goBack();
                        }
                      }}
                    >
                      Go Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading || dragging || !isValid || !dirty}
                    >
                      {
                        loading
                          ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                              <span className="sr-only">Processing...</span>
                            </>
                          )
                          : (<>Submit</>)
                      }
                    </button>
                  </div>
                </div>
              </Form>
            );
          }
        }
      </Formik>
      <ErrorBody />
      <ConfirmationModal
        onClose={() => {
          // setError('');
          // setErrorsArray([]);
          setShowError(false);
        }}
        onConfirm={() => {
          // setError('');
          // setErrorsArray([]);
          setShowError(false);
        }}
        title="Error"
        message={error}
        show={showError}
        setShow={setShowError}
        isSimple={true}
        ModalForm={ErrorBody}
      />
      <ConfirmationModal
        onClose={navigateOrCloseModalBasedOnErrorState}
        onConfirm={navigateOrCloseModalBasedOnErrorState}
        onConfirmButtonText="Finish"
        title="Success!"
        message={modalMessage}
        show={showModal}
        setShow={setShowModal}
        isSimple={true}
      />
      <ConfirmationModal
        onClose={() => {
          setLoading(false);
          setModalMessage('');
          setShowConfirm(false);
        }}
        title="Bulk Set Fields"
        message={modalMessage}
        show={showConfirm}
        setShow={setShowConfirm}
        onConfirmAsync={onConfirmSubmit}
        useSwal={false}
      />
    </div>
  );
};

export default AdsBulkEdit;
